import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { UsersIcon } from "lucide-react";

import { COMMAND_ID as SELECT_ENTITY_COMMAND_ID } from "@/app/app/_/commands/SelectEntityCommand";
import { Buttons } from "@/components/atoms/Buttons";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Mutations } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useScope } from "@/hooks/useScope";
import { CommandCenterContextEntityPicker } from "@/layouts/AppLayout/CommandCenter/CommandCenterContextEntityPicker";
import { State } from "@/state";
import { Field, FieldLabel } from "@/components/ui/field";
import { Typography } from "@/components/ui/typography";
import { Spinner } from "@/components/ui/spinner";
import { Input } from "@/components/ui/input";

import { Switch } from "@/components/ui/switch";

export const COMMAND_ID = "invite-member";

export type InviteMemberContextType = {
  entityIdentifier?: string;
};

function InviteMemberContextPicker({
  commandInstanceId,
  context,
}: {
  commandInstanceId: string;
  context?: InviteMemberContextType;
}) {
  const { accounts, scopedActiveEntityIdentifier } = useScope();
  const updateCommandContext = State((state) => state.updateCommandContext);

  const ownerEntityIdentifiers = useMemo(
    () =>
      accounts
        ?.filter((account) => account.isOwner && account.isEntityAccount)
        .map((account) => account.entity!.identifier) ?? [],
    [accounts],
  );

  const entityIdentifier =
    context?.entityIdentifier ??
    (ownerEntityIdentifiers.includes(scopedActiveEntityIdentifier)
      ? scopedActiveEntityIdentifier
      : undefined);

  return (
    <CommandCenterContextEntityPicker
      entityIdentifier={entityIdentifier}
      activeEntityIdentifiers={ownerEntityIdentifiers}
      setEntityIdentifier={(identifier) => {
        updateCommandContext(commandInstanceId, {
          entityIdentifier: identifier,
        });
      }}
    />
  );
}

function Component({
  commandInstanceId,
  context,
}: {
  commandInstanceId: string;
  context?: InviteMemberContextType;
}) {
  const clearCommandStack = State((store) => store.clearCommandStack);
  const popCommandsFromStack = State((store) => store.popCommandsFromStack);
  const pushToCommandStack = State((store) => store.pushToCommandStack);
  const updateCommandContext = State((store) => store.updateCommandContext);
  const { accounts, scopedActiveEntityIdentifier, isLoading } = useScope();

  const [email, setEmail] = useState("");
  const [isOwnerInvite, setIsOwnerInvite] = useState(false);

  const ownerEntityIdentifiers = useMemo(
    () =>
      accounts
        ?.filter((account) => account.isOwner && account.isEntityAccount)
        .map((account) => account.entity!.identifier) ?? [],
    [accounts],
  );

  const entityIdentifier =
    context?.entityIdentifier ??
    (ownerEntityIdentifiers.includes(scopedActiveEntityIdentifier)
      ? scopedActiveEntityIdentifier
      : undefined);

  const hasOwnerEntities = ownerEntityIdentifiers.length > 0;
  const isValidOwnerEntity = Boolean(
    entityIdentifier && ownerEntityIdentifiers.includes(entityIdentifier),
  );

  useEffect(() => {
    if (isLoading || !hasOwnerEntities || isValidOwnerEntity) {
      return;
    }

    if (ownerEntityIdentifiers.length === 1) {
      updateCommandContext(commandInstanceId, {
        entityIdentifier: ownerEntityIdentifiers[0],
      });
      return;
    }

    popCommandsFromStack();
    pushToCommandStack({
      commandDefinitionId: SELECT_ENTITY_COMMAND_ID,
      context: {
        entityIdentifiers: ownerEntityIdentifiers,
        onSubmit: (entity) => {
          pushToCommandStack({
            commandDefinitionId: COMMAND_ID,
            context: { entityIdentifier: entity.identifier },
          });
        },
      },
    });
  }, [
    commandInstanceId,
    entityIdentifier,
    hasOwnerEntities,
    isLoading,
    isValidOwnerEntity,
    ownerEntityIdentifiers,
    popCommandsFromStack,
    pushToCommandStack,
    updateCommandContext,
  ]);

  const { mutate: inviteUser, loading: inviteLoading } = useBackendMutation({
    mutation: Mutations.INVITE_USER_TO_ENTITY,
    refetchQueries: ["User", "PendingInvitations", "Accounts"],
  });

  const handleInvite = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed || !entityIdentifier) {
      return;
    }

    await inviteUser({
      variables: {
        input: {
          entityIdentifier,
          emailAddress: trimmed,
          isOwner: isOwnerInvite,
        },
      },
      callbacks: {
        onSuccess: () => {
          setEmail("");
          State.setState({ snackbarMessage: "Invitation sent." });
          clearCommandStack();
        },
      },
    });
  }, [clearCommandStack, email, entityIdentifier, inviteUser, isOwnerInvite]);

  if (!isLoading && !hasOwnerEntities) {
    return (
      <div className="p-4">
        <Typography level="body-md" className="text-muted-foreground">
          Only entity owners can invite members.
        </Typography>
      </div>
    );
  }

  if (!isValidOwnerEntity) {
    return null;
  }

  return (
    <StickyLayout
      stickyBottomChildren={
        <Buttons
          buttons={[
            {
              variant: "outline",
              children: "Cancel",
              onClick: () => clearCommandStack(),
            },
            {
              variant: "default",
              children: <>{inviteLoading && <Spinner />}Send invitation</>,
              onClick: () => void handleInvite(),
              disabled: inviteLoading || !email.trim(),
            },
          ]}
        />
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleInvite();
        }}
        className="flex flex-col gap-4 p-4"
      >
        <Typography level="body-sm" className="text-muted-foreground">
          Send an email invitation to join this entity.
        </Typography>
        <Field>
          <FieldLabel htmlFor="invite-member-email">Email address</FieldLabel>
          <Input
            id="invite-member-email"
            autoFocus
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="colleague@company.com"
          />
        </Field>
        <div className="flex items-center gap-2">
          <Switch
            checked={isOwnerInvite}
            onCheckedChange={(checked) => setIsOwnerInvite(checked === true)}
          />
          <Typography level="body-sm">Invite as owner</Typography>
        </div>
      </form>
    </StickyLayout>
  );
}

export function InviteMemberCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );
  const updateCommandDefinition = State(
    (state) => state.updateCommandDefinition,
  );
  const { accounts, isLoading } = useScope();

  const hasOwnerEntity = useMemo(
    () =>
      accounts?.some((account) => account.isOwner && account.isEntityAccount) ??
      false,
    [accounts],
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Invite member",
      description: "Invite someone to join an entity by email",
      getLabel: () => "Invite member",
      ReactIcon: UsersIcon,
      group: "Objects",
      onClick: () => {
        const scopedEntityIdentifier = State.getState().scopedEntityIdentifier;
        State.getState().pushToCommandStack({
          commandDefinitionId: COMMAND_ID,
          context: scopedEntityIdentifier
            ? { entityIdentifier: scopedEntityIdentifier }
            : {},
        });
      },
      component: ({ commandInstanceId, context }) => (
        <Component
          commandInstanceId={commandInstanceId}
          context={context as InviteMemberContextType | undefined}
        />
      ),
      context: ({ commandInstanceId, context }) => (
        <InviteMemberContextPicker
          commandInstanceId={commandInstanceId}
          context={context as InviteMemberContextType | undefined}
        />
      ),
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, [registerCommandDefinition, unregisterCommandDefinition]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    updateCommandDefinition(COMMAND_ID, { isHidden: !hasOwnerEntity });
  }, [hasOwnerEntity, isLoading, updateCommandDefinition]);

  return null;
}
